import { useState } from 'react';

import { getContentUrl } from '@ecp/common/src/utils/utils';
import { IMAGE_STATUS } from '@ecp/common/src/const/imageFile';

import CommonApi from '@bo-apis/common/commonApi';

export const useImageFileUploader = (defaultFile) => {
  const [imageFileList, setImageFileList] = useState(defaultFile ? [defaultFile] : []);
  const saveImageFile = async ({ imageFileParam } = {}) => {
    let imageFiles = imageFileList;
    if (imageFileParam) imageFiles = imageFileParam;

    const newFiles = imageFiles.filter(({ status }) => status === IMAGE_STATUS.NEW);

    const results = await Promise.all(
      newFiles.map(({ file }) => {
        const data = new FormData();
        data.append('fileList', file);
        return CommonApi.uploadPublicFiles(data);
      })
    );

    results.forEach(({ result }, idx) => {
      newFiles[idx].fileId = result[0].fileId;
      newFiles[idx].fileS3Url = result[0].fileS3Url;
    });

    let idx = -1;
    const updatedImageFileList = imageFiles.map((imageFile) => {
      if (imageFile.status !== IMAGE_STATUS.NEW) {
        return imageFile;
      } else {
        idx += 1;
        return newFiles[idx];
      }
    });

    const copyFiles = imageFiles.filter(({ status }) => status === IMAGE_STATUS.COPY);
    if (copyFiles && copyFiles.length > 0) {
      const { result: copied } = await CommonApi.copyPublicFiles(copyFiles);
      const updatedImageFileListWithCopy = [
        ...updatedImageFileList.filter((imageFile) => imageFile.status !== IMAGE_STATUS.COPY),
        ...copied,
      ];
      setImageFileList(updatedImageFileListWithCopy);
      return updatedImageFileListWithCopy;
    } else {
      setImageFileList(updatedImageFileList);
      return updatedImageFileList;
    }
  };

  const saveImageFileGroup = async (access = 'public', groupId) => {
    const uploadFiles = access === 'public' ? CommonApi.uploadPublicFiles : CommonApi.uploadProtectedFiles;

    const newFiles = imageFileList.filter(({ status }) => status === IMAGE_STATUS.NEW);
    const noNewFilesId = imageFileList.reduce((acc, { status, fileId }) => {
      if (status !== IMAGE_STATUS.NEW) {
        acc.push(fileId);
      }
      return acc;
    }, []);
    if (newFiles.length <= 0) return imageFileList;

    const data = new FormData();
    data.append('groupId', groupId || '');
    newFiles.map(({ file }) => data.append('fileList', file));
    const { result } = await uploadFiles(data);
    const createdFiles = result.reduce((acc, file) => {
      if (!noNewFilesId.includes(file.fileId)) {
        acc.push(file);
      }
      return acc;
    }, []);

    let idx = -1;
    const updatedImageFileList = imageFileList.map((imageFile) => {
      if (imageFile.status !== IMAGE_STATUS.NEW) return imageFile;
      else {
        idx += 1;
        return {
          ...imageFile,
          ...createdFiles[idx],
          type: createdFiles[idx].fileId,
          status: IMAGE_STATUS.REGISTERED,
          url: getContentUrl(createdFiles[idx].fileGroupId, `${createdFiles[idx].name}.${createdFiles[idx].ext}`),
        };
      }
    });
    setImageFileList(updatedImageFileList);

    return updatedImageFileList;
  };

  const deleteImageFile = async ({ imageFileParam } = {}) => {
    let imageFiles = imageFileList;
    if (imageFileParam) imageFiles = imageFileParam;

    const delFiles = imageFiles.filter(({ status }) => status === IMAGE_STATUS.NEED_TO_DEL);
    const noDelFiles = imageFiles.filter(({ status }) => status !== IMAGE_STATUS.NEED_TO_DEL);
    if (delFiles.length <= 0) return;

    await CommonApi.deleteFileList(delFiles.map(({ fileId }) => ({ fileId })));

    const updatedImageFileList = [...noDelFiles];
    setImageFileList(updatedImageFileList);

    return updatedImageFileList;
  };

  const rollbackImageFile = async () => {
    const delFiles = imageFileList.filter(({ status, fileId }) => status === IMAGE_STATUS.NEW && fileId);
    const noDelFiles = imageFileList.filter(({ status, fileId }) => !(status === IMAGE_STATUS.NEW && fileId));
    if (delFiles.length <= 0) return;

    await CommonApi.deleteFileList(delFiles.map(({ fileId }) => ({ fileId })));

    const updatedImageFileList = [...noDelFiles];
    setImageFileList(updatedImageFileList);

    return updatedImageFileList;
  };

  return { imageFileList, setImageFileList, saveImageFile, saveImageFileGroup, deleteImageFile, rollbackImageFile };
};
